/**
 * RP4 — Text mesh factory functions extracted from `Scene3DStage.tsx`.
 *
 * Pure builders for SDF (instanced quad), SHX (LineSegments2 strokes),
 * and missing-font placeholder text/mtext meshes.
 *
 * Companion to `sceneMeshFactories.ts` — split out so each module stays
 * under the 400 LOC cap.
 *
 * No behavior changes — pure code move from `Scene3DStage.tsx`.
 */

import * as THREE from "three";
import { mtextLineAdvance } from "@dxf-ai-editor/drawing-model";
import {
  LineSegments2,
  LineSegmentsGeometry,
  LineMaterial,
} from "../rendering/thickLines";

import type { RenderableEntity, SceneGeometry } from "../types";
import { directMeshLayerStamp } from "../layerChainVisibility";
import { shouldSkipTextRun } from "../text/textScreenGate";
import type { SdfAtlas } from "../text/sdfAtlasLoader";
import { createTextMaterial } from "../text/textMaterial";
import { renderTextRun, type TextRunInput } from "../text/textRunRenderer";
import { renderShxTextRun } from "../text/shxRunRenderer";
import type { ShxStrokeFont } from "../text/shxStrokeAtlas";

/**
 * PR-E2 — build a `THREE.InstancedMesh` for a text or mtext renderable.
 *
 * Each glyph is one quad; we feed per-instance attributes (offset,
 * scale, color, atlas-UVs, height) into the SDF text material from
 * `textMaterial.ts`. The base geometry is a unit quad reused across
 * all instances — the vertex shader scales + offsets it per glyph.
 *
 * The mesh's local transform applies the entity's world position,
 * rotation, and (for MText) per-line stacking. PR-E3 will replace
 * the trivial `cursorY -= lineHeight` stacking with a paragraph-
 * aware layout walker.
 */
export function buildTextEntityMesh(
  renderable: RenderableEntity,
  atlas: SdfAtlas,
  cameraZoom?: number,
): THREE.Object3D | null {
  const g = renderable.geometry;
  if (g.kind !== "text" && g.kind !== "mtext") return null;

  // PR-V5 — entity-level sub-pixel gate. `g.height` is the entity's
  // primary height; for MText every run can override but the entity
  // height is the upper bound on visual size. If even this max height
  // falls below 0.5 px on screen, no run can possibly be perceived —
  // skip the mesh build entirely. The "dot" representation is owned by
  // the entity-level Points accumulator on the caller side; here we
  // just signal "skip".
  if (cameraZoom !== undefined) {
    const bucket = shouldSkipTextRun(g.height, cameraZoom);
    if (bucket === "skip" || bucket === "dot") return null;
  }

  // Compose the run list. Single-line Text becomes one synthetic
  // run; MText already carries its run array (PR-E2 simple split).
  const runs: TextRunInput[] =
    g.kind === "text"
      ? [
          {
            text: g.content,
            heightWorld: g.height,
            widthFactor: g.widthFactor,
            colorHex: renderable.colorHex,
            cameraZoom,
          },
        ]
      : g.runs.map<TextRunInput>((r) => ({
          text: r.text,
          heightWorld: r.heightWorld,
          widthFactor: r.widthFactor,
          colorHex: r.colorHex ?? renderable.colorHex,
          // PR-E3 — forward the per-run formatting fields the
          // textRunRenderer now consumes.
          trackingFactor: r.trackingFactor,
          oblique: r.oblique,
          underline: r.underline,
          overline: r.overline,
          strikethrough: r.strikethrough,
          baselineOffsetWorld: r.baselineOffsetWorld,
          cameraZoom,
        }));

  // Walk the runs, advancing cursorY per run for paragraph stacking.
  // PR-E3 — empty-text runs flagged as paragraph breaks (`text === ""`)
  // bump the line counter without emitting glyph quads. Other runs
  // contribute glyphs at the cursor X / Y of the current line. The
  // layout still does not handle inline-on-a-single-line concatenation
  // beyond the cursor advance returned by `renderTextRun`; PR-E5 lands
  // that path together with the SHX integration.
  // The baseline step is the ONE-truth `mtextLineAdvance`
  // (`@dxf-ai-editor/drawing-model`), not a local `× 1.6`: `wcsBoundsText.ts`'s
  // `\Q` shear bound has to know where line `k` lands in order to cover its
  // painted vertices, and a second copy of the constant is exactly what let the
  // two drift (@codex PR #794 round 4 P2). Same expression, same order of
  // multiplication — bit-identical output.
  const lineHeight =
    g.kind === "mtext" ? mtextLineAdvance(g.height, g.lineSpacingFactor) : 0;
  const accOffsets: number[] = [];
  const accScales: number[] = [];
  const accColors: number[] = [];
  const accHeights: number[] = [];
  const accUvMin: number[] = [];
  const accUvMax: number[] = [];
  const accObliques: number[] = [];
  const accDecorations: number[] = [];
  let totalInstances = 0;
  let lineIndex = 0;
  let lineCursorX = 0;
  for (let li = 0; li < runs.length; li++) {
    const run = runs[li];
    if (!run) continue;
    if (run.text === "") {
      // Empty-text run = paragraph break. Bump line, reset cursor X.
      lineIndex += 1;
      lineCursorX = 0;
      continue;
    }
    const cursorY = -lineHeight * lineIndex;
    const built = renderTextRun(
      { ...run, cursorStartX: lineCursorX, cursorStartY: cursorY },
      atlas,
    );
    lineCursorX = built.cursorEndX;
    if (built.instanceCount === 0) continue;
    for (let i = 0; i < built.offsets.length; i++)
      accOffsets.push(built.offsets[i] ?? 0);
    for (let i = 0; i < built.scales.length; i++)
      accScales.push(built.scales[i] ?? 0);
    for (let i = 0; i < built.colors.length; i++)
      accColors.push(built.colors[i] ?? 0);
    for (let i = 0; i < built.heights.length; i++)
      accHeights.push(built.heights[i] ?? 0);
    for (let i = 0; i < built.uvMin.length; i++)
      accUvMin.push(built.uvMin[i] ?? 0);
    for (let i = 0; i < built.uvMax.length; i++)
      accUvMax.push(built.uvMax[i] ?? 0);
    for (let i = 0; i < built.obliques.length; i++)
      accObliques.push(built.obliques[i] ?? 0);
    for (let i = 0; i < built.decorations.length; i++)
      accDecorations.push(built.decorations[i] ?? 0);
    totalInstances += built.instanceCount;
  }
  if (totalInstances === 0) return null;

  // Unit-quad geometry: 4 verts at (0,0)-(1,0)-(1,1)-(0,1) +
  // 6 indices (two triangles). This is what the vertex shader reads
  // as `position.xy` and lerps against per-instance UV/scale.
  const geometry = new THREE.InstancedBufferGeometry();
  const positions = new Float32Array([0, 0, 0, 1, 0, 0, 1, 1, 0, 0, 1, 0]);
  geometry.setAttribute(
    "position",
    new THREE.BufferAttribute(positions, 3),
  );
  geometry.setIndex([0, 1, 2, 0, 2, 3]);
  geometry.instanceCount = totalInstances;

  geometry.setAttribute(
    "instance_offset",
    new THREE.InstancedBufferAttribute(new Float32Array(accOffsets), 2),
  );
  geometry.setAttribute(
    "instance_scale",
    new THREE.InstancedBufferAttribute(new Float32Array(accScales), 2),
  );
  geometry.setAttribute(
    "instance_color",
    new THREE.InstancedBufferAttribute(new Float32Array(accColors), 3),
  );
  geometry.setAttribute(
    "instance_uv_min",
    new THREE.InstancedBufferAttribute(new Float32Array(accUvMin), 2),
  );
  geometry.setAttribute(
    "instance_uv_max",
    new THREE.InstancedBufferAttribute(new Float32Array(accUvMax), 2),
  );
  geometry.setAttribute(
    "instance_height",
    new THREE.InstancedBufferAttribute(new Float32Array(accHeights), 1),
  );
  // PR-E3 — oblique (radians) + decoration flag (0/1) per instance.
  geometry.setAttribute(
    "instance_oblique",
    new THREE.InstancedBufferAttribute(new Float32Array(accObliques), 1),
  );
  geometry.setAttribute(
    "instance_decoration",
    new THREE.InstancedBufferAttribute(new Float32Array(accDecorations), 1),
  );

  const material = createTextMaterial(atlas, renderable.colorHex);

  const mesh = new THREE.Mesh(geometry, material);
  // Apply entity-level position + rotation to the mesh (the run
  // renderer emitted glyph offsets in text-local space).
  mesh.position.set(g.position.x, g.position.y, g.position.z);
  if (g.rotation !== 0) {
    mesh.rotation.z = (g.rotation * Math.PI) / 180;
  }
  // Codex #342 F1 — mirrored-text glyph flip. The mesh is anchored at the entity
  // insertion (mesh.position) and glyph offsets are text-local, so a local-axis
  // sign on mesh.scale flips the glyph frame about that anchor — the same pivot
  // convention as the legacy SVG renderShxText (anchor-pivot mirrorX/mirrorY).
  // Applied ONLY when the flag is set ⇒ non-mirrored text keeps scale (1,1,1)
  // (golden parity). TEXT: backward = local-X reverse (group-71 bit 2),
  // upsideDown = local-Y flip (bit 4). MTEXT: extrusion z<0 (no group-71) ⇒
  // upside-down/local-Y flip (the geometryTransforms bit-4 equivalence).
  applyMirrorScale(mesh, g);
  // Codex S9b P2 — `renderParentId` present ⇒ this is a synthetic DIMENSION /
  // MULTILEADER label; stamp the REAL parent id so the scene-traversal
  // visibility passes (viewport mask, base layout pre-hide) + hit-test resolve
  // it to the parent that actually lives in the spatial index. Real text keeps
  // its own id (`renderParentId` absent).
  mesh.userData = {
    entityId: renderable.renderParentId ?? renderable.id,
    // Codex PR #784 P1 — the renderable's OWN layer (a satellite child's, not
    // the parent's) + its block-reference layer chain (round 3) for the
    // per-viewport frozen-layer gate (`viewportRenderPass.ts
    // isDirectMeshOnFrozenLayer`).
    ...directMeshLayerStamp(renderable),
    kind: g.kind,
    fontName: atlas.fontName,
  };
  return mesh;
}

/**
 * Codex #342 F1 — apply the mirrored-text local-axis scale flip to a text /
 * mtext mesh. TEXT carries group-71 `backward` (reverse reading = local-X sign)
 * + `upsideDown` (flip up = local-Y sign); MTEXT carries `mirrored` (extrusion
 * z < 0 = handedness flip, modelled as the local-Y / upside-down equivalent,
 * matching `geometryTransforms.ts` bit-4 derivation). No-op when no flag is set,
 * so non-mirrored entities stay byte-identical (golden parity). Shared one truth
 * for both the SDF/TTF and SHX mesh paths (no scale-math clone — jscpd gate).
 */
function applyMirrorScale(mesh: THREE.Object3D, g: SceneGeometry): void {
  if (g.kind === "text") {
    const sx = g.backward ? -1 : 1;
    const sy = g.upsideDown ? -1 : 1;
    if (sx !== 1 || sy !== 1) mesh.scale.set(sx, sy, 1);
  } else if (g.kind === "mtext") {
    if (g.mirrored) mesh.scale.set(1, -1, 1);
  }
}

/**
 * PR-E5 — build a `THREE.LineSegments` mesh for a text or mtext
 * renderable backed by an SHX stroke font. SHX glyphs are vector-only
 * (lines + arcs); arcs are pre-tessellated to polylines in
 * `shxStrokeAtlas` so the renderer just needs to walk the run's
 * pre-flattened segments and pack them into a flat Float32 vertex
 * buffer.
 *
 * The mesh's local transform applies the entity's world position,
 * rotation, and (for MText) per-line stacking — exactly the same
 * convention as `buildTextEntityMesh`.
 */
export function buildShxTextEntityMesh(
  renderable: RenderableEntity,
  font: ShxStrokeFont,
  cameraZoom?: number,
): THREE.Object3D | null {
  const g = renderable.geometry;
  if (g.kind !== "text" && g.kind !== "mtext") return null;

  // PR-V5 — entity-level sub-pixel gate. Mirrors `buildTextEntityMesh`.
  if (cameraZoom !== undefined) {
    const bucket = shouldSkipTextRun(g.height, cameraZoom);
    if (bucket === "skip" || bucket === "dot") return null;
  }

  const runs: TextRunInput[] =
    g.kind === "text"
      ? [
          {
            text: g.content,
            heightWorld: g.height,
            widthFactor: g.widthFactor,
            colorHex: renderable.colorHex,
            // SHX-specific: oblique is per-entity for single-line
            // TEXT; MText runs already carry their own oblique.
            oblique: (g.oblique * Math.PI) / 180,
            cameraZoom,
          },
        ]
      : g.runs.map<TextRunInput>((r) => ({
          text: r.text,
          heightWorld: r.heightWorld,
          widthFactor: r.widthFactor,
          colorHex: r.colorHex ?? renderable.colorHex,
          trackingFactor: r.trackingFactor,
          oblique: r.oblique,
          underline: r.underline,
          overline: r.overline,
          strikethrough: r.strikethrough,
          baselineOffsetWorld: r.baselineOffsetWorld,
          cameraZoom,
        }));

  // Walk runs left-to-right, advancing cursorY per paragraph break
  // (same convention as buildTextEntityMesh).
  // Shared baseline step — see `buildTextEntityMesh`'s note above.
  const lineHeight =
    g.kind === "mtext" ? mtextLineAdvance(g.height, g.lineSpacingFactor) : 0;
  const allPositions: Float32Array[] = [];
  let totalSegments = 0;
  let lineIndex = 0;
  let lineCursorX = 0;
  for (let li = 0; li < runs.length; li++) {
    const run = runs[li];
    if (!run) continue;
    if (run.text === "") {
      // Paragraph break.
      lineIndex += 1;
      lineCursorX = 0;
      continue;
    }
    const cursorY = -lineHeight * lineIndex;
    const built = renderShxTextRun(
      { ...run, cursorStartX: lineCursorX, cursorStartY: cursorY },
      font,
    );
    lineCursorX = built.cursorEndX;
    if (built.segmentCount === 0) continue;
    allPositions.push(built.positions);
    totalSegments += built.segmentCount;
  }
  if (totalSegments === 0) return null;

  // Concatenate per-run vertex buffers into a single Float32Array.
  // (One alloc per concat keeps GC pressure flat versus repeated
  // Array.push.)
  const merged = new Float32Array(totalSegments * 6);
  let offset = 0;
  for (const buf of allPositions) {
    merged.set(buf, offset);
    offset += buf.length;
  }

  // LineSegmentsGeometry uses the same per-vertex format the line-
  // bucket meshes use, so the GPU pipeline is shared. Z-component is
  // always 0 for 2D mode (PR-A; PR-L lifts to 3D).
  const geometry = new LineSegmentsGeometry();
  geometry.setPositions(merged);

  const color = new THREE.Color(renderable.colorHex);
  const material = new LineMaterial({
    color: color.getHex(),
    // 1px screen-space line width — matches the SVG SHX renderer's
    // 1-stroke-pixel emission. PR-I will replace this with the
    // per-entity zoom-aware lineweight used by other line buckets.
    linewidth: 1,
    worldUnits: false,
    dashed: false,
    transparent: false,
    // LineMaterial's `resolution` uniform is updated per-frame in the
    // SceneContent useFrame hook. We set an initial value here so the
    // first paint isn't size-zero.
    resolution: new THREE.Vector2(1, 1),
  });

  const mesh = new LineSegments2(geometry, material);
  mesh.computeLineDistances();
  // Apply entity-level position + rotation. The run renderer emitted
  // segment endpoints in text-local space; the mesh transform does the
  // entity-level translate + rotate.
  mesh.position.set(g.position.x, g.position.y, g.position.z);
  if (g.rotation !== 0) {
    mesh.rotation.z = (g.rotation * Math.PI) / 180;
  }
  // Codex #342 F1 — mirrored-text glyph flip (same one-truth as the SDF/TTF
  // path; anchor-pivot local-axis scale). No-op when no flag is set.
  applyMirrorScale(mesh, g);
  mesh.userData = {
    entityId: renderable.renderParentId ?? renderable.id,
    ...directMeshLayerStamp(renderable),
    kind: g.kind,
    fontName: font.fontName,
    isShx: true,
  };
  return mesh;
}

/**
 * PR-E2 / Q15.C — build a placeholder rectangle mesh for a text/
 * mtext entity when its atlas is missing. Empty rectangle of
 * `text.height × content.length × widthFactor`. The user sees an
 * outlined box where the text would render so they know glyphs are
 * present even if the font isn't installed.
 */
export function buildTextPlaceholderMesh(
  renderable: RenderableEntity,
): THREE.Object3D | null {
  const g = renderable.geometry;
  if (g.kind !== "text" && g.kind !== "mtext") return null;

  const content =
    g.kind === "text" ? g.content : g.runs.map((r) => r.text).join("");
  const len = Math.max(content.length, 1);
  const width = g.height * g.widthFactor * len * 0.6;
  const height = g.height;

  const geometry = new THREE.PlaneGeometry(width, height);
  const material = new THREE.MeshBasicMaterial({
    color: new THREE.Color(renderable.colorHex).getHex(),
    transparent: true,
    opacity: 0.25,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  const mesh = new THREE.Mesh(geometry, material);
  // GitHub Codex review PR #558 round 2 (round-11 local read-only
  // follow-up) P2 — "Apply mirroring to missing-font placeholders". The
  // REAL glyph mesh (`buildTextMesh`/`buildDimensionOrMLeaderTextMesh`)
  // applies `applyMirrorScale`'s local-axis flip via `mesh.scale`, which
  // moves the RENDERED extent to the OPPOSITE side of the anchor for a
  // mirrored/backward run (a Dimension/MLeader's mirrored text sits BELOW
  // its anchor, `upsideDown`; a backward TEXT's glyphs sit to its LEFT).
  // This placeholder built its OWN geometry directly (no shared mesh
  // scale to inherit), always offsetting `+width/2`/`+height/2` — the
  // SAME anchor-side bug the real glyph mesh had before `applyMirrorScale`
  // existed. `mtext`-kind content (Dimension/Multileader's synthesized
  // text) only ever carries the Y-only `mirrored` flag; `text`-kind
  // (standalone TEXT/ATTRIB) carries the INDEPENDENT `backward`/
  // `upsideDown` pair (`textGeometry.ts`'s 2-bit decode).
  const upsideDown = g.kind === "text" ? (g.upsideDown ?? false) : (g.mirrored ?? false);
  const backward = g.kind === "text" ? (g.backward ?? false) : false;
  const widthSign = backward ? -1 : 1;
  const heightSign = upsideDown ? -1 : 1;
  // Plane is centred on origin; offset to match insertion-baseline
  // anchoring (lower-left corner at insertion point — same anchor as
  // the Roboto-Regular glyph baseline; the mirrored/backward corner
  // otherwise, matching the real glyph mesh's flipped extent).
  //
  // GitHub Codex review PR #558 round 2 (round-13 local read-only
  // follow-up) P2 — "Rotate placeholder offsets in the text frame". The
  // REAL glyph mesh bakes its anchor↔glyph offset into the GEOMETRY
  // itself (local, pre-rotation) so `mesh.rotation.z` rotates the
  // already-anchored shape as one rigid unit (`buildTextEntityMesh`'s own
  // comment: "glyph offsets are text-local"). This placeholder instead
  // bakes the offset into `mesh.position` (a WORLD-space translation
  // added AFTER THREE's local rotation is applied to the plane's own,
  // origin-centred vertices) — for `g.rotation === 0` the two are
  // equivalent, but for a rotated entity the anchor↔center offset is
  // itself a LOCAL-frame vector that must rotate WITH the text, exactly
  // like `rotateLineStackOffset`'s own local-pitch-offset rotation
  // (`@dxf-ai-editor/command-engine`) for the same reason. Rotate the
  // signed offset by `g.rotation` before adding it to `g.position`.
  const rotRad = (g.rotation * Math.PI) / 180;
  const localDx = (widthSign * width) / 2;
  const localDy = (heightSign * height) / 2;
  const cos = Math.cos(rotRad);
  const sin = Math.sin(rotRad);
  mesh.position.set(
    g.position.x + localDx * cos - localDy * sin,
    g.position.y + localDx * sin + localDy * cos,
    0,
  );
  if (g.rotation !== 0) {
    mesh.rotation.z = rotRad;
  }
  mesh.userData = {
    entityId: renderable.renderParentId ?? renderable.id,
    ...directMeshLayerStamp(renderable),
    kind: g.kind,
    placeholder: true,
  };
  return mesh;
}
